import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import * as Papa from 'papaparse';

import { BuyersListService } from '@core/services/buyers-list.service';

export type CsvPreview = { headers: string[]; rows: string[][] };
export type BuyersCsvMapping = {
  firstName?: number | null;
  lastName?: number | null;
  phone?: number | null;
  email?: number | null;
};

export const MAX_BUYERS_LIST_ROWS = 5000;

@Component({
  selector: 'app-upload-buyers-home',
  templateUrl: './home.component.html',
})
export class UploadBuyersHomeComponent implements OnInit {
  protected file: File | null = null;
  protected name = '';
  protected hasValidName = false;
  protected hasHeader: 'yes' | 'no' | undefined = undefined;
  protected csvPreview: CsvPreview | null = null;
  protected columnMapping: BuyersCsvMapping = {};
  protected isMappingComplete = false;

  protected uploading = false;
  protected uploaded = false;

  protected loadingAllowed = true;
  protected allowedAPIError = false;
  protected isAllowed = false;
  protected numberOfLists = 0;
  protected maxRows = MAX_BUYERS_LIST_ROWS;

  constructor(
    private toastr: ToastrService,
    private buyersListService: BuyersListService
  ) {}

  ngOnInit(): void {
    this.buyersListService.isBuyListCreationAllowed().subscribe({
      next: (result) => {
        const data = JSON.parse(result.isBuyListCreationAllowed);

        this.loadingAllowed = false;
        this.isAllowed = !!data?.value;
        this.allowedAPIError = false;

        const match = data?.message?.match(/\d+/);
        this.numberOfLists = match ? parseInt(match[0], 10) : 0;

        if (this.isAllowed) {
          this.buyersListService
            .allBuyListFromOrganization({})
            .subscribe((listsResult) => {
              if (listsResult.allBuyListFromOrganization.count) {
                this.numberOfLists =
                  listsResult.allBuyListFromOrganization.count;
              }
            });
        }
      },
      error: (error) => {
        this.loadingAllowed = false;
        this.allowedAPIError = true;
      },
    });
  }

  handleFileSelected(file: File) {
    const reader = new FileReader();

    reader.onload = (event) => {
      const content = event.target?.result as string;
      const lineCount = content.split(/\r\n|\r|\n/).length;

      if (lineCount > this.maxRows + 1) {
        this.toastr.error(`CSV file must have ${this.maxRows} rows or fewer.`);
        this.clearSelectedFile();
        return;
      }

      this.file = file;
    };

    reader.readAsText(file);
  }

  clearSelectedFile() {
    Object.assign(this, {
      file: null,
      name: '',
      hasValidName: false,
      hasHeader: undefined,
      csvPreview: null,
      columnMapping: {},
      isMappingComplete: false,
      uploaded: false,
    });
  }

  /**
   * Parse file only if:
   * Has not being parsed, both name and hasHeader are set
   */
  parseCSV(file: File) {
    if (!this.file || this.csvPreview) return;
    if (!this.hasHeader || !this.hasValidName) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const csvContent = event.target?.result as string;
      let headers: string[] = [];
      let rows: string[][] = [];

      // Use PapaParse’s step function for streaming (parses row-by-row instead of loading everything at once)
      Papa.parse<string[]>(csvContent, {
        step: (row, parser) => {
          if (headers.length === 0) {
            headers = row.data as string[];
          } else if (rows.length < 5) {
            rows.push(row.data as string[]);
          } else {
            parser.abort();
          }
        },
        complete: () => {
          this.csvPreview = { headers, rows };
          this.verifyIsMappingComplete(this.columnMapping);
        },
        skipEmptyLines: true,
      });
    };
    reader.readAsText(file);
  }

  handleMappingChange(mapping: BuyersCsvMapping) {
    this.columnMapping = mapping;
    this.verifyIsMappingComplete(mapping);
  }

  onNameChange($event: string) {
    this.name = $event;
    this.hasValidName = this.name.length >= 3 && this.name.length <= 30;
    this.parseCSV(this.file as File);
    this.verifyIsMappingComplete(this.columnMapping);
  }

  onHasHeaderChange($event: 'yes' | 'no') {
    this.hasHeader = $event;
    if (!this.csvPreview && this.hasValidName) {
      this.parseCSV(this.file as File);
      this.verifyIsMappingComplete(this.columnMapping);
    }
  }

  verifyIsMappingComplete(mapping: BuyersCsvMapping) {
    this.isMappingComplete = Object.entries(mapping)
      .filter(([key]) => key !== 'lastName') // Exclude lastName
      .some(([_, index]) => index === null || index === undefined)
      ? false
      : true;
  }
}
